import { useState } from 'react';
import { Flex, Icon, Autocomplete, TextField, Spinner, Select, Text } from '@scale/scaleui-radix';
import { faSearch } from '@fortawesome/pro-regular-svg-icons';
import useDebouncedValue from 'react-scale/hooks/useDebouncedValue';
import { IListItem } from 'react-scale/components/WebinarSessions/types';
import { useQuery } from '@tanstack/react-query-v4';
import EnrichedError from 'react-scale/lib/enrichedError';
import apiFetch from '@scale/frontend-lib/api-fetch';
import { IAudience } from '@scale/shared/types/audience';
import { isNil } from 'lodash';

interface SingleAudienceAutoCompleteProps {
  audienceId: string;
  onAudienceIdChange: (id: string | null) => void;
  audienceBucket: number | null;
  setAudienceBucket: (bucket: number | null) => void;
}

const useLookupAudienceById = ({ audienceId }: { audienceId: string }) => {
  return useQuery<IAudience | null>({
    queryKey: ['useLookupAudienceById', audienceId],
    queryFn: async () => {
      if (!audienceId) {
        return null;
      }
      const url = `corp-api/audiences/${audienceId}`;
      try {
        const { audience } = await apiFetch<{
          audience: IAudience;
          editable: boolean;
          deletable: boolean;
        }>(null, url);
        return audience;
      } catch (error) {
        throw new EnrichedError(
          error.status,
          "Couldn't fetch audience",
          error.response?.message ?? error.toString(),
          'Error fetching audience',
          url,
        );
      }
    },
  });
};

const useLookupAudiences = ({ searchText }: { searchText: string }) => {
  return useQuery<IListItem[]>({
    queryKey: ['useLookupAudiences', searchText],
    queryFn: async () => {
      const url = `corp-api/audiences`;
      try {
        const { items } = await apiFetch<{
          items: IAudience[];
          itemCount: number;
          page: number;
          pageSize: number;
        }>(null, url, {
          query: {
            search: searchText,
          },
        });
        return items.map(item => ({
          id: item._id,
          name: item.name,
        }));
      } catch (error) {
        throw new EnrichedError(
          error.status,
          "Couldn't fetch audiences",
          error.response?.message ?? error.toString(),
          'Error fetching audiences',
          url,
        );
      }
    },
  });
};
export const SingleAudienceAutoComplete = ({
  audienceId,
  onAudienceIdChange,
  audienceBucket,
  setAudienceBucket,
}: SingleAudienceAutoCompleteProps) => {
  const [searchText, setSearchText] = useState('');
  const debouncedSearchText = useDebouncedValue(searchText, 500);

  // Fetches the audiences to show up as options based on the search input
  const { data: audienceData, isFetching: isFetchingAudienceData } = useLookupAudiences({
    searchText: debouncedSearchText,
  });
  const audienceOptions = audienceData?.map((audience: IListItem) => ({
    value: audience.id,
    label: audience.name,
  }));

  // Fetch the audience info for the currently selected audience
  const { data: audienceInfo, isFetching: isFetchingAudienceInfo } = useLookupAudienceById({
    audienceId,
  });

  // TODO: Once Radix is migrated to 1.36, we can use the AsyncItems and AsyncValues to replace the loading state
  return (
    <Flex direction="column" gap="2">
      <Autocomplete.Root
        search={searchText}
        onSearchChange={setSearchText}
        value={audienceId}
        onValueChange={(newValue: string) => {
          onAudienceIdChange(newValue);
        }}
      >
        <Autocomplete.Content>
          <Autocomplete.TextField
            placeholder="Search by audience name..."
            // Do not allow invalid values from paste
            onPaste={() => {}}
          >
            <TextField.Slot side="left">
              <Icon icon={faSearch} label="Search" />
            </TextField.Slot>
            <Autocomplete.Values>
              {audienceId.length > 0 && (
                <Autocomplete.Value value={audienceId}>
                  {isFetchingAudienceInfo || isNil(audienceInfo) ? <Spinner /> : audienceInfo.name}
                </Autocomplete.Value>
              )}
            </Autocomplete.Values>
            <Autocomplete.Reset />
          </Autocomplete.TextField>
          <Autocomplete.Items>
            {isFetchingAudienceData
              ? [
                  <Autocomplete.Item
                    key={'fetching-audiences'}
                    keywords={searchText}
                    value={searchText}
                    disabled
                    style={{ pointerEvents: 'none' }}
                  >
                    <Flex direction="row" gap="2">
                      <Spinner />
                      {'Fetching audiences...'}
                    </Flex>
                  </Autocomplete.Item>,
                ]
              : (audienceOptions ?? []).map(audience => (
                  <Autocomplete.Item key={audience.value} value={audience.value}>
                    {audience.label}
                  </Autocomplete.Item>
                ))}
          </Autocomplete.Items>
        </Autocomplete.Content>
      </Autocomplete.Root>
      {audienceInfo && (
        <Flex direction="column" gap="2">
          <Text as="label" size="1" weight="medium">
            Bucket
          </Text>
          <Select.Root
            size="2"
            value={audienceBucket?.toString() ?? 'No bucket selected'}
            onValueChange={(value: string) => {
              setAudienceBucket(Number(value));
            }}
          >
            <Select.Trigger />
            <Select.Content>
              {audienceInfo.buckets.map((bucket, bucketIndex) => (
                <Select.Item
                  key={`${bucketIndex}-${bucket.percentage}`}
                  value={bucketIndex.toString()}
                >
                  {`Bucket ${bucketIndex + 1} (${bucket.percentage}%)`}
                </Select.Item>
              ))}
            </Select.Content>
          </Select.Root>
        </Flex>
      )}
    </Flex>
  );
};
